// <img> that shows the canonical/candidate image and transparently falls back
// to the original photo if the candidate URL fails to load. CONTRACTS §10.

import { useEffect, useState } from "react";

export interface ArtImageProps {
  /** Primary URL to try (e.g. canonical). */
  src: string | undefined;
  /** Fallback URL if `src` is missing or errors (e.g. original). */
  fallbackSrc?: string | undefined;
  alt: string;
  className?: string;
  /**
   * Fired when the image can't be shown at all — i.e. after the fallback (if any)
   * has also failed, or when there's no usable URL. Lets a parent detect a broken
   * original photo and offer a "re-attach" affordance (continuation 2026-06-07 P1b).
   */
  onBroken?: () => void;
}

export function ArtImage({ src, fallbackSrc, alt, className, onBroken }: ArtImageProps) {
  const initial = src ?? fallbackSrc;
  const [current, setCurrent] = useState<string | undefined>(initial);
  const [usedFallback, setUsedFallback] = useState(!src && !!fallbackSrc);

  // Reset when inputs change (e.g. after re-enrich swaps the canonical key).
  useEffect(() => {
    setCurrent(src ?? fallbackSrc);
    setUsedFallback(!src && !!fallbackSrc);
  }, [src, fallbackSrc]);

  // No usable URL at all is itself a "broken" state for the parent's purposes.
  useEffect(() => {
    if (!current) onBroken?.();
    // Only re-fire when the resolved URL changes, not when onBroken's identity does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  if (!current) {
    return <div className={`art-image art-image--empty ${className ?? ""}`}>no image</div>;
  }

  return (
    <img
      className={`art-image ${className ?? ""}`}
      src={current}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => {
        // Try the fallback once; if that's already what we're showing (or there
        // is none), the image is unrecoverable — tell the parent.
        if (!usedFallback && fallbackSrc && current !== fallbackSrc) {
          setUsedFallback(true);
          setCurrent(fallbackSrc);
        } else {
          onBroken?.();
        }
      }}
    />
  );
}
